import React, { useState, useRef, useEffect } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const MatchChat = ({ messages = [], matchId, onSendMessage }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e?.preventDefault();

    if (!newMessage?.trim()) return;

    setIsTyping(true);

    // Send message via callback
    onSendMessage?.(newMessage?.trim());

    // Clear input
    setNewMessage('');
    setIsTyping(false);

    // Focus back to input
    inputRef?.current?.focus();
  };

  const MessageBubble = ({ message }) => {
    const isSystemMessage = message?.type === 'system' || message?.type === 'weather';
    const isCurrentUser = message?.user?.name === 'Você';

    if (isSystemMessage) {
      return (
        <div className="flex justify-center my-3">
          <div className="bg-muted/50 border border-border rounded-lg px-3 py-2 max-w-xs">
            <div className="flex items-center space-x-2">
              <Icon
                name={message?.type === 'weather' ? 'Cloud' : 'Info'}
                size={14}
                className="text-muted-foreground" />

              <p className="text-xs text-muted-foreground">
                {message?.content}
              </p>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-1">
              {formatDistanceToNow(new Date(message?.timestamp), {
                addSuffix: true,
                locale: ptBR
              })}
            </p>
          </div>
        </div>);

    }

    return (
      <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`flex items-start space-x-2 max-w-sm ${isCurrentUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
          {/* Avatar */}
          {!isCurrentUser &&
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {message?.user?.avatar ?
            <img
              src={message?.user?.avatar}
              alt={message?.user?.avatarAlt || `Avatar de ${message?.user?.name}`}
              className="w-full h-full object-cover" /> :


            <Icon name="User" size={14} className="text-muted-foreground" />
            }
            </div>
          }

          {/* Message Content */}
          <div>
            {/* User Name */}
            {!isCurrentUser &&
            <p className="text-xs text-muted-foreground mb-1 px-1">
                {message?.user?.name}
              </p>
            }
            
            {/* Message Bubble */}
            <div className={`rounded-lg px-3 py-2 ${
            isCurrentUser ?
            'bg-primary text-primary-foreground' :
            'bg-muted text-card-foreground'}`
            }>
              <p className="text-sm">
                {message?.content}
              </p>
            </div>
            
            {/* Timestamp */}
            <p className={`text-xs text-muted-foreground mt-1 px-1 ${
            isCurrentUser ? 'text-right' : 'text-left'}`
            }>
              {format(new Date(message?.timestamp), 'HH:mm', { locale: ptBR })}
            </p>
          </div>
        </div>
      </div>);

  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 flex flex-col h-96">
      {/* Chat Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold text-card-foreground">Chat do Jogo

            </h3>
            <p className="text-sm text-muted-foreground">
              {messages?.length || 0} mensagens
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Online Status Indicator */}
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-success rounded-full"></div>
              <span className="text-xs text-muted-foreground">
                3 online
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Messages Area */}
      <div className="flex-1 p-4 overflow-y-auto">
        {messages?.length > 0 ?
        <div>
            {messages?.map((message) =>
          <MessageBubble
            key={message?.id}
            message={message} />

          )}
            
            {/* Typing Indicator */}
            {isTyping &&
          <div className="flex justify-start mb-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <Icon name="User" size={14} className="text-muted-foreground" />
                  </div>
                  <div className="bg-muted rounded-lg px-3 py-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
          }
            
            <div ref={messagesEndRef} />
          </div> :

        <div className="text-center py-8">
            <div className="flex items-center justify-center w-12 h-12 bg-muted/50 rounded-lg mx-auto mb-3">
              <Icon name="MessageCircle" size={20} className="text-muted-foreground" />
            </div>
            <h4 className="font-medium text-card-foreground mb-1">
              Nenhuma mensagem ainda
            </h4>
            <p className="text-sm text-muted-foreground">
              Seja o primeiro a enviar uma mensagem
            </p>
          </div>
        }
      </div>
      {/* Message Input */}
      <div className="p-4 border-t border-border">
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          <div className="flex-1">
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e?.target?.value)}
              placeholder="Digite sua mensagem..."
              maxLength={300}
              className="border-0 bg-muted/50 focus:bg-background" />

          </div>
          
          <Button
            type="submit"
            variant="default"
            size="sm"
            iconName="Send"
            disabled={!newMessage?.trim() || isTyping}
            loading={isTyping}
            className="px-3">

            {isTyping ? null : 'Enviar'}
          </Button>
        </form>

        {/* Quick Actions */}
        <div className="flex space-x-2 mt-3">
          <Button
            variant="ghost"
            size="sm"
            iconName="Users"
            onClick={() => onSendMessage?.('Quem já confirmou presença?')}
            className="text-xs h-7 px-2">

            Lista presença
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            iconName="MapPin"
            onClick={() => onSendMessage?.('Como chegar no local?')}
            className="text-xs h-7 px-2">

            Localização
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            iconName="Clock"
            onClick={() => onSendMessage?.('Que horas vamos começar?')}
            className="text-xs h-7 px-2">

            Horário
          </Button>
        </div>

        {/* Character Counter */}
        {newMessage?.length > 0 &&
        <div className="flex justify-end mt-1">
            <span className={`text-xs ${
          newMessage?.length > 250 ?
          'text-error' :
          newMessage?.length > 200 ?
          'text-warning' : 'text-muted-foreground'}`
          }>
              {newMessage?.length}/300
            </span>
          </div>
        }
      </div>
    </div>);

};

export default MatchChat;